import React, { useEffect, useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { toast } from "react-toastify";
import api from "../api/axios";
import FormPreview from "./FormPreview";


const PAGE_NAME = "Forms";

const safeArray = (value) => {
  if (Array.isArray(value)) return value;

  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
};

const normaliseForm = (form) => {
  if (!form) return null;

  return {
    ...form,
    fields: (form.fields || [])
      .map((field) => ({
        ...field,
        options: safeArray(field.options),
        allowed_file_types: safeArray(field.allowed_file_types),
        sort_order: Number(field.sort_order || 0),
      }))
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)),
  };
};

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  /*
   * The Axios interceptor opens the global 403 dialog.
   * Avoid showing a duplicate toast.
   */
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message ||
    fallbackMessage
  );
};

const FormViewSidebar = ({
  visible,
  onHide,
  form,
  subCategoryId,
  subCategoryName,
  onEdit,
}) => {
  const [linkedForm, setLinkedForm] = useState(null);
  const [loading, setLoading] = useState(false);

  const activeForm = useMemo(
    () => normaliseForm(form || linkedForm),
    [form, linkedForm]
  );

  const fetchFormBySubCategory = async () => {
    if (!visible || form || !subCategoryId) return;

    try {
      setLoading(true);

      const res = await api.get(`/admin/forms/sub-category/${subCategoryId}`,
        {
          headers:
            getPermissionHeaders("view"),
        }
      );
      const data = res.data?.data || null;

      setLinkedForm(data);

      if (!data) {
        toast.info("No active form is linked with this sub-category.");
      }
    } catch (error) {
      showRequestError(
        error,
        "Failed to fetch the linked form. Please try again."
      );
      setLinkedForm(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormBySubCategory();
  }, [visible, subCategoryId, form]);

  useEffect(() => {
    if (!visible) {
      setLinkedForm(null);
      setLoading(false);
    }
  }, [visible]);

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      className="premium-form-view-sidebar"
    >
      <div className="form-view-shell">
        {loading && (
          <div className="form-view-empty">
            <div className="premium-loader"></div>
            <h3>Loading form</h3>
            <p>Please wait while the linked form is prepared.</p>
          </div>
        )}

        {!loading && !activeForm && (
          <div className="form-view-empty">
            <h3>No form linked</h3>
            <p>
              {subCategoryName
                ? `No active form is linked with ${subCategoryName}.`
                : "No active form is linked with this sub-category."}
            </p>
          </div>
        )}

        {!loading && activeForm && (
          <>
            <FormPreview form={activeForm} />

            <div className="form-view-actions">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={onHide}
              >
                Close
              </button>

              {onEdit && (
                <button
                  type="button"
                  className="primary-action"
                  onClick={() => onEdit(activeForm)}
                >
                  Edit Form
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default FormViewSidebar;